import {
    AfterContentInit,
    ChangeDetectionStrategy,
    Component,
    ContentChildren,
    ElementRef,
    Inject,
    Input,
    NgZone,
    OnDestroy,
    QueryList,
    Renderer2
} from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { PSplitterAreaComponent } from './p-splitter-area.component';
import { Subscription } from 'rxjs';

export type PSplitterDirection = 'horizontal' | 'vertical';

@Component({
    standalone: true,
    selector: 'p-splitter',
    template: '<ng-content></ng-content>',
    styleUrls: ['./p-splitter.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class PSplitterComponent implements AfterContentInit, OnDestroy {
    @Input() direction: PSplitterDirection = 'horizontal';
    @Input() gutterSize = 8;

    @ContentChildren(PSplitterAreaComponent) areas!: QueryList<PSplitterAreaComponent>;

    private gutterElements: HTMLElement[] = [];
    private gutterUnlisteners: (() => void)[] = [];
    private dragging = false;
    private dragIndex = -1;
    private startPos = 0;
    private startSizes: number[] = [];
    private totalSize = 0;

    private mouseMoveListener: (() => void) | null = null;
    private mouseUpListener: (() => void) | null = null;
    private touchMoveListener: (() => void) | null = null;
    private touchEndListener: (() => void) | null = null;
    private areasChangeSub: Subscription | null = null;
    private areaVisibilitySubs: Subscription[] = [];
    private dragOverlay: HTMLElement | null = null;

    constructor(
        private readonly zone: NgZone,
        private readonly renderer: Renderer2,
        private readonly elementRef: ElementRef<HTMLElement>,
        @Inject(DOCUMENT) private readonly document: Document
    ) {}

    get isHorizontal(): boolean {
        return this.direction === 'horizontal';
    }

    ngAfterContentInit(): void {
        this.updateLayout();
        this.subscribeToAreaVisibility();
        this.areasChangeSub = this.areas.changes.subscribe(() => {
            this.updateLayout();
            this.subscribeToAreaVisibility();
        });
    }

    ngOnDestroy(): void {
        this.removeDragOverlay();
        this.removeGlobalListeners();
        this.areasChangeSub?.unsubscribe();
        this.unsubscribeAreaVisibility();
        this.removeGutters();
    }

    private subscribeToAreaVisibility(): void {
        this.unsubscribeAreaVisibility();
        this.areas.forEach((area) => {
            this.areaVisibilitySubs.push(area.visibilityChange.subscribe(() => this.updateLayout()));
        });
    }

    private unsubscribeAreaVisibility(): void {
        this.areaVisibilitySubs.forEach((s) => s.unsubscribe());
        this.areaVisibilitySubs = [];
    }

    private updateLayout(): void {
        this.removeGutters();
        this.updateHostClass();
        this.createGutters();
        this.applyAreaStyles();
    }

    private updateHostClass(): void {
        const el = this.elementRef.nativeElement;
        this.renderer.removeClass(el, 'p-splitter-horizontal');
        this.renderer.removeClass(el, 'p-splitter-vertical');
        this.renderer.addClass(el, `p-splitter-${this.direction}`);
    }

    private getVisibleAreas(): PSplitterAreaComponent[] {
        return this.areas?.filter((a) => a.visible) ?? [];
    }

    private createGutters(): void {
        const visibleAreas = this.getVisibleAreas();
        const hostEl = this.elementRef.nativeElement;

        for (let i = 0; i < visibleAreas.length - 1; i++) {
            const gutter = this.renderer.createElement('div');
            this.renderer.addClass(gutter, 'p-splitter-gutter');
            this.renderer.addClass(gutter, this.isHorizontal ? 'p-splitter-gutter-horizontal' : 'p-splitter-gutter-vertical');

            if (this.isHorizontal) {
                this.renderer.setStyle(gutter, 'width', `${this.gutterSize}px`);
            } else {
                this.renderer.setStyle(gutter, 'height', `${this.gutterSize}px`);
            }

            const handle = this.renderer.createElement('div');
            this.renderer.addClass(handle, 'p-splitter-gutter-handle');
            this.renderer.appendChild(gutter, handle);

            // Insert gutter after the corresponding visible area
            const areaEl = visibleAreas[i].elementRef.nativeElement;
            this.renderer.insertBefore(hostEl, gutter, areaEl.nextSibling);

            const gutterIndex = i;
            const unlistenMouse = this.renderer.listen(gutter, 'mousedown', (e: MouseEvent) => this.onGutterMouseDown(e, gutterIndex));
            const unlistenTouch = this.renderer.listen(gutter, 'touchstart', (e: TouchEvent) => this.onGutterTouchStart(e, gutterIndex));

            this.gutterElements.push(gutter);
            this.gutterUnlisteners.push(unlistenMouse, unlistenTouch);
        }
    }

    private removeGutters(): void {
        this.gutterUnlisteners.forEach((u) => u());
        this.gutterUnlisteners = [];
        this.gutterElements.forEach((g) => g.parentNode?.removeChild(g));
        this.gutterElements = [];
    }

    private applyAreaStyles(): void {
        if (!this.areas) return;

        const visibleAreas = this.getVisibleAreas();
        const totalGutterSize = Math.max(0, visibleAreas.length - 1) * this.gutterSize;

        // Normalize sizes so visible areas sum to 100
        const totalSize = visibleAreas.reduce((sum, a) => sum + a.size, 0);
        const normalizedSizes = visibleAreas.map((a) => (totalSize > 0 ? (a.size / totalSize) * 100 : 100 / visibleAreas.length));

        let visibleIndex = 0;
        this.areas.forEach((area) => {
            const el = area.elementRef.nativeElement;
            if (!area.visible) {
                this.renderer.setStyle(el, 'flex-basis', '0');
                this.renderer.setStyle(el, 'flex-grow', '0');
                this.renderer.setStyle(el, 'flex-shrink', '0');
                return;
            }
            const pct = normalizedSizes[visibleIndex];
            const gutterDeduction = totalGutterSize / visibleAreas.length;
            this.renderer.setStyle(el, 'flex-basis', `calc(${pct}% - ${gutterDeduction}px)`);
            this.renderer.setStyle(el, 'flex-grow', '0');
            this.renderer.setStyle(el, 'flex-shrink', '0');
            visibleIndex++;
        });
    }

    private onGutterMouseDown(event: MouseEvent, gutterIndex: number): void {
        event.preventDefault();
        this.startDrag(gutterIndex, this.isHorizontal ? event.clientX : event.clientY);
    }

    private onGutterTouchStart(event: TouchEvent, gutterIndex: number): void {
        if (event.touches.length === 1) {
            event.preventDefault();
            const touch = event.touches[0];
            this.startDrag(gutterIndex, this.isHorizontal ? touch.clientX : touch.clientY);
        }
    }

    private startDrag(gutterIndex: number, startPos: number): void {
        this.dragging = true;
        this.dragIndex = gutterIndex;
        this.startPos = startPos;

        const visibleAreas = this.getVisibleAreas();
        this.startSizes = visibleAreas.map((a) => a.size);
        this.totalSize = this.isHorizontal ? this.elementRef.nativeElement.clientWidth : this.elementRef.nativeElement.clientHeight;

        this.areas.forEach((area) => this.renderer.setStyle(area.elementRef.nativeElement, 'transition', 'none'));
        this.createDragOverlay();

        this.zone.runOutsideAngular(() => {
            this.mouseMoveListener = this.renderer.listen('document', 'mousemove', (e: MouseEvent) => this.onDrag(e));
            this.mouseUpListener = this.renderer.listen('document', 'mouseup', () => this.onDragEnd());
            this.touchMoveListener = this.renderer.listen('document', 'touchmove', (e: TouchEvent) => this.onTouchDrag(e));
            this.touchEndListener = this.renderer.listen('document', 'touchend', () => this.onDragEnd());
        });
    }

    private createDragOverlay(): void {
        const overlay = this.renderer.createElement('div');
        this.renderer.setStyle(overlay, 'position', 'fixed');
        this.renderer.setStyle(overlay, 'top', '0');
        this.renderer.setStyle(overlay, 'left', '0');
        this.renderer.setStyle(overlay, 'width', '100%');
        this.renderer.setStyle(overlay, 'height', '100%');
        this.renderer.setStyle(overlay, 'z-index', '2147483647');
        this.renderer.setStyle(overlay, 'cursor', this.isHorizontal ? 'col-resize' : 'row-resize');
        this.renderer.appendChild(this.document.body, overlay);
        this.dragOverlay = overlay;
    }

    private removeDragOverlay(): void {
        if (this.dragOverlay) {
            this.renderer.removeChild(this.document.body, this.dragOverlay);
            this.dragOverlay = null;
        }
    }

    private onDrag(event: MouseEvent): void {
        if (!this.dragging) return;
        this.handleDrag(this.isHorizontal ? event.clientX : event.clientY);
    }

    private onTouchDrag(event: TouchEvent): void {
        if (!this.dragging || event.touches.length !== 1) return;
        const touch = event.touches[0];
        this.handleDrag(this.isHorizontal ? touch.clientX : touch.clientY);
    }

    private handleDrag(currentPos: number): void {
        const delta = currentPos - this.startPos;
        const totalGutterSize = Math.max(0, this.getVisibleAreas().length - 1) * this.gutterSize;
        const availableSize = this.totalSize - totalGutterSize;

        if (availableSize <= 0) return;

        const totalStartSize = this.startSizes.reduce((a, b) => a + b, 0);
        const deltaPct = (delta / availableSize) * totalStartSize;

        const visibleAreas = this.getVisibleAreas();
        const areaA = visibleAreas[this.dragIndex];
        const areaB = visibleAreas[this.dragIndex + 1];
        if (!areaA || !areaB) return;

        const combinedSize = this.startSizes[this.dragIndex] + this.startSizes[this.dragIndex + 1];

        if (areaA.minSize + areaB.minSize > combinedSize) return;

        let newSizeA = this.startSizes[this.dragIndex] + deltaPct;
        let newSizeB = this.startSizes[this.dragIndex + 1] - deltaPct;

        // Enforce constraints
        if (newSizeA < areaA.minSize) {
            newSizeA = areaA.minSize;
            newSizeB = combinedSize - newSizeA;
        }
        if (newSizeA > areaA.maxSize) {
            newSizeA = areaA.maxSize;
            newSizeB = combinedSize - newSizeA;
        }
        if (newSizeB < areaB.minSize) {
            newSizeB = areaB.minSize;
            newSizeA = combinedSize - newSizeB;
        }
        if (newSizeB > areaB.maxSize) {
            newSizeB = areaB.maxSize;
            newSizeA = combinedSize - newSizeB;
        }

        areaA.size = newSizeA;
        areaB.size = newSizeB;
        areaA.sizeChange.emit(newSizeA);
        areaB.sizeChange.emit(newSizeB);
        this.applyAreaStyles();
    }

    private onDragEnd(): void {
        this.dragging = false;
        this.dragIndex = -1;
        this.areas.forEach((area) => this.renderer.removeStyle(area.elementRef.nativeElement, 'transition'));
        this.removeDragOverlay();
        this.removeGlobalListeners();
    }

    private removeGlobalListeners(): void {
        this.mouseMoveListener?.();
        this.mouseUpListener?.();
        this.touchMoveListener?.();
        this.touchEndListener?.();
        this.mouseMoveListener = null;
        this.mouseUpListener = null;
        this.touchMoveListener = null;
        this.touchEndListener = null;
    }
}
